import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import translations from '../data/translations';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

export default function ReferencesSection() {
  const { lang } = useLanguage();
  const t = translations.references[lang];
  const [lightbox, setLightbox] = useState(null);

  const Card = ({ item, i }) => (
    <motion.div
      key={i}
      initial={{ opacity: 0, y: 25 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: i * 0.12 }}
      data-testid={`reference-card-${i}`}
      className="group relative bg-enbi-navy overflow-hidden hover:shadow-2xl transition-all duration-500 cursor-pointer"
      onClick={() => setLightbox(item)}
    >
      <div className="aspect-[4/3] overflow-hidden">
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
      </div>
      <div className="p-6">
        <span className="text-enbi-gold text-[10px] tracking-[0.25em] uppercase font-semibold">
          {item.category}
        </span>
        <h3 className="font-outfit text-white text-base font-semibold uppercase tracking-tight mt-2 mb-2">
          {item.title}
        </h3>
        <p className="text-white/50 text-sm leading-relaxed">
          {item.desc}
        </p>
      </div>
    </motion.div>
  );

  return (
    <section id="references" data-testid="references-section" className="relative py-24 md:py-32 bg-enbi-offwhite overflow-hidden">
      <div
        className="absolute top-0 left-0 w-[35%] h-full bg-enbi-navy/[0.03]"
        style={{ clipPath: 'polygon(0 0, 100% 0, 60% 100%, 0 100%)' }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-10 h-[2px] bg-enbi-gold" />
            <span className="text-enbi-gold text-xs tracking-[0.3em] uppercase font-semibold">{t.label}</span>
            <div className="w-10 h-[2px] bg-enbi-gold" />
          </div>
          <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-semibold text-enbi-navy uppercase tracking-tight mb-4">
            {t.headline}
          </h2>
          <p className="text-slate-500 text-base max-w-2xl mx-auto">{t.sub}</p>
        </motion.div>

        {/* Top row: 3 items */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {t.items.slice(0, 3).map((item, i) => (
            <Card key={i} item={item} i={i} />
          ))}
        </div>

        {/* Bottom row: 3 items */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {t.items.slice(3, 6).map((item, i) => (
            <Card key={i + 3} item={item} i={i + 3} />
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 md:p-8 cursor-pointer"
            onClick={() => setLightbox(null)}
            data-testid="lightbox-overlay"
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-10"
              data-testid="lightbox-close"
            >
              <X size={28} strokeWidth={1.5} />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.25 }}
              src={lightbox.image}
              alt={lightbox.title}
              className="max-w-full max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-image"
            />
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center" onClick={(e) => e.stopPropagation()}>
              <span className="text-enbi-gold text-[10px] tracking-[0.25em] uppercase font-semibold">{lightbox.category}</span>
              <h3 className="font-outfit text-white text-lg font-semibold uppercase tracking-tight mt-1">{lightbox.title}</h3>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
